export type FormatDateOptions = Intl.DateTimeFormatOptions & {
  locale?: string;
};

/**
 * Format a number using the given locale (e.g. from i18n.language).
 * Use the app's selected language so formatting is consistent with user settings.
 */
export function formatNumber(
  value: number,
  opts: Intl.NumberFormatOptions & { locale?: string } = {},
): string {
  const { locale, ...numberOpts } = opts;
  return value.toLocaleString(locale ?? undefined, {
    minimumFractionDigits: numberOpts.minimumFractionDigits ?? 0,
    maximumFractionDigits: numberOpts.maximumFractionDigits ?? 0,
    ...numberOpts,
  });
}

/**
 * Use this function to format a number to a volume in cubic meters.
 *
 * Cubic meters are not supported by the Intl.NumberFormatOptions.
 */
export function formatNumberToVolume(
  value: number,
  opts: Omit<
    Intl.NumberFormatOptions,
    'style' | 'currency' | 'currencyDisplay' | 'unit' | 'unitDisplay'
  > & { locale?: string } = {},
): string {
  const { locale, ...numberOpts } = opts;
  return `${(value).toLocaleString(locale ?? undefined, {
    minimumFractionDigits: numberOpts.minimumFractionDigits ?? 0,
    maximumFractionDigits: numberOpts.maximumFractionDigits ?? 2,
    ...numberOpts,
  })} m³`;
}

export function formatDate(
  date: Date | string | number | undefined,
  opts: FormatDateOptions = {},
) {
  if (!date) {
    return '';
  }

  const { locale = 'en-US', ...dateTimeOpts } = opts;

  try {
    return new Intl.DateTimeFormat(locale, {
      month: dateTimeOpts.month ?? 'long',
      day: dateTimeOpts.day ?? 'numeric',
      year: dateTimeOpts.year ?? 'numeric',
      ...dateTimeOpts,
    }).format(new Date(date));
  } catch {
    return '';
  }
}

export function formatLocalizedDate(
  value: string | null | undefined,
  locale: string,
  opts: Omit<FormatDateOptions, 'locale'> = {},
) {
  return value
    ? formatDate(value, {
        locale,
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        ...opts,
      })
    : null;
}

export function formatLocalizedDateTime(
  value: string | null | undefined,
  locale: string,
) {
  return formatLocalizedDate(value, locale, {
    hour: '2-digit',
    minute: '2-digit',
  });
}

const CURRENCY_SYMBOLS: Record<string, string> = {
  EUR: '€',
  USD: '$',
};

export function getCurrencySymbol(currency?: string | null): string {
  if (!currency) {
    return '€';
  }
  return CURRENCY_SYMBOLS[currency] ?? currency;
}

export function formatCurrencyAmount(
  amount?: { totalAmount: number; currency: string } | null,
) {
  if (!amount) {
    return '—';
  }
  const formattedAmount = amount.totalAmount.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  const currencySymbol = getCurrencySymbol(amount.currency);
  return `${formattedAmount} ${currencySymbol}`;
}

export function formatPrice(amount: number, currency: string): string {
  const formattedAmount = amount.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  const currencySymbol = getCurrencySymbol(currency);
  return `${formattedAmount} ${currencySymbol}`;
}
